import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, Phone, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const EMPTY = {
    full_name: "",
    date_of_birth: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    mobile_phone: "",
    sms_opt_in: false,
    ssn: "",
};

export default function Profile() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [ssnMasked, setSsnMasked] = useState("");
    const [completed, setCompleted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [numbers, setNumbers] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/numbers/my");
                setNumbers(data);
            } catch {}
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile");
                setForm((f) => ({
                    ...f,
                    full_name: data.full_name || "",
                    date_of_birth: data.date_of_birth || "",
                    street_address: data.street_address || "",
                    city: data.city || "",
                    state: data.state || "",
                    zip_code: data.zip_code || "",
                    mobile_phone: data.mobile_phone || "",
                    sms_opt_in: !!data.sms_opt_in,
                }));
                setSsnMasked(data.ssn_masked || "");
                setCompleted(!!data.profile_completed);
            } catch {}
        })();
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/profile", form);
            toast.success("Profile saved");
            // Full reload so auth context picks up profile_completed
            const next = new URLSearchParams(window.location.search).get("next");
            window.location.assign(next === "billing" ? "/billing" : "/profile");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save profile");
            setSaving(false);
        }
    };

    return (
        <div className="space-y-8 max-w-3xl" data-testid="profile-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Account</p>
                <h1 className="font-display text-4xl font-bold tracking-tight">Customer profile</h1>
                <p className="text-zinc-600 mt-2">
                    Federal telecom rules require us to verify who is using our phone services. Your information is
                    encrypted and never shared.
                </p>
            </div>

            {completed ? (
                <div className="border border-[#C4B5FD] bg-[#F5F0FF] rounded-sm p-4 flex items-center gap-3" data-testid="profile-complete-banner">
                    <ShieldCheck className="w-5 h-5 text-emerald-deep" />
                    <p className="text-sm">Your profile is complete. You can update it any time.</p>
                </div>
            ) : (
                <div className="border border-[#D97706] bg-[#FEF3C7] rounded-sm p-4" data-testid="profile-incomplete-banner">
                    <p className="text-sm font-medium text-[#D97706]">
                        Your profile must be completed before you can subscribe to a plan.
                    </p>
                </div>
            )}

            {/* Subscription & purchased numbers */}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper" data-testid="profile-service-card">
                <div className="px-6 py-4 border-b border-[#DDD6FE] flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-emerald-deep" />
                    <h2 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Your subscription</h2>
                </div>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase text-zinc-500">Plan</p>
                        <p className="font-display text-2xl capitalize mt-1" data-testid="profile-plan">{user?.plan || "—"}</p>
                    </div>
                    <div>
                        <p className="font-mono text-[10px] uppercase text-zinc-500">Billing</p>
                        <p className="font-display text-2xl capitalize mt-1">{user?.billing_cycle || "—"}</p>
                    </div>
                    <div>
                        <p className="font-mono text-[10px] uppercase text-zinc-500">Renews</p>
                        <p className="font-display text-2xl mt-1">
                            {user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : "—"}
                        </p>
                    </div>
                </div>
                <div className="px-6 pb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Phone className="w-4 h-4 text-emerald-deep" />
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                            Numbers on this subscription ({numbers.length})
                        </h3>
                    </div>
                    {numbers.length === 0 ? (
                        <div className="border border-dashed border-[#DDD6FE] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="profile-no-numbers">
                            <p className="text-sm text-zinc-600">No phone number yet — pick one to start texting and calling.</p>
                            <Button onClick={() => navigate("/numbers")} data-testid="profile-get-number-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-sm h-9 shrink-0">
                                Get my number
                            </Button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#DDD6FE]/60 border border-[#DDD6FE] rounded-sm" data-testid="profile-numbers-list">
                            {numbers.map((n) => (
                                <li key={n.number_id || n.phone_number} className="flex items-center justify-between px-4 py-3">
                                    <div>
                                        <span className="font-mono text-sm font-medium">{n.phone_number}</span>
                                        {n.friendly_name && n.friendly_name !== n.phone_number && (
                                            <span className="ml-2 text-xs text-zinc-500">{n.friendly_name}</span>
                                        )}
                                    </div>
                                    <span className="font-mono text-[10px] uppercase text-zinc-500">
                                        purchased {n.purchased_at ? new Date(n.purchased_at).toLocaleDateString() : "—"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <form onSubmit={save} className="border border-[#DDD6FE] rounded-sm bg-paper p-6 sm:p-8 space-y-5">
                <Field label="Full legal name">
                    <Input value={form.full_name} onChange={set("full_name")} required data-testid="profile-full-name" placeholder="Jane A. Smith" className="rounded-sm border-[#DDD6FE]" />
                </Field>
                <Field label="Date of birth">
                    <Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} required data-testid="profile-dob" className="rounded-sm border-[#DDD6FE]" />
                </Field>
                <Field label="Street address">
                    <Input value={form.street_address} onChange={set("street_address")} required data-testid="profile-street" placeholder="123 Main St, Apt 4" className="rounded-sm border-[#DDD6FE]" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="City">
                        <Input value={form.city} onChange={set("city")} required data-testid="profile-city" className="rounded-sm border-[#DDD6FE]" />
                    </Field>
                    <Field label="State">
                        <Input value={form.state} onChange={set("state")} required data-testid="profile-state" placeholder="TN" maxLength={2} className="rounded-sm border-[#DDD6FE] uppercase" />
                    </Field>
                    <Field label="ZIP code">
                        <Input value={form.zip_code} onChange={set("zip_code")} required data-testid="profile-zip" placeholder="38103" className="rounded-sm border-[#DDD6FE]" />
                    </Field>
                </div>
                <Field label="Mobile phone (for account notifications)">
                    <Input
                        type="tel"
                        value={form.mobile_phone}
                        onChange={set("mobile_phone")}
                        required
                        data-testid="profile-mobile-phone"
                        placeholder="(901) 555-0123"
                        className="rounded-sm border-[#DDD6FE]"
                        inputMode="tel"
                    />
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.sms_opt_in}
                            onChange={(e) => setForm((f) => ({ ...f, sms_opt_in: e.target.checked }))}
                            data-testid="profile-sms-opt-in"
                            className="mt-0.5 accent-[#4C1D95]"
                        />
                        <span className="text-sm text-zinc-600">
                            Text me account notifications (payments, my number, subscription reminders). Msg &amp; data
                            rates may apply. Reply STOP to opt out anytime.
                        </span>
                    </label>
                </Field>
                <Field label={ssnMasked ? `Social Security Number (on file: ${ssnMasked})` : "Social Security Number"}>
                    <div className="relative">
                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            value={form.ssn}
                            onChange={set("ssn")}
                            required={!completed}
                            data-testid="profile-ssn"
                            placeholder={ssnMasked ? "Enter new SSN to change" : "123-45-6789"}
                            className="rounded-sm border-[#DDD6FE] pl-9"
                            inputMode="numeric"
                        />
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">Stored encrypted (AES-128). Only visible to authorized admins.</p>
                </Field>
                <Button type="submit" disabled={saving} data-testid="profile-save-btn" className="bg-espresso text-[#FAF5FF] hover:bg-zinc-800 rounded-sm h-10 w-full sm:w-auto px-8">
                    {saving ? "Saving…" : "Save profile"}
                </Button>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">{label}</label>
            {children}
        </div>
    );
}
